import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { candidatesApi } from '../services/candidates';
import { logout } from './authSlice';

export const fetchCandidates = createAsyncThunk(
    'candidates/fetchList',
    async ({ skip = 0, limit = 20, org_id, filters = {} }, { rejectWithValue }) => {
        try {
            return await candidatesApi.getList(skip, limit, org_id, filters);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchCandidateById = createAsyncThunk(
    'candidates/fetchById',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await candidatesApi.getById(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchCandidatesByVacancy = createAsyncThunk(
    'candidates/fetchByVacancy',
    async ({ vacancyId, skip = 0, limit = 20 }, { rejectWithValue }) => {
        try {
            return await candidatesApi.getByVacancy(vacancyId, skip, limit);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const createCandidate = createAsyncThunk(
    'candidates/create',
    async ({ orgId, candidate }, { rejectWithValue }) => {
        try {
            const result = await candidatesApi.create(orgId, candidate);

            // Проверяем, есть ли ошибка в ответе (когда статус 200, но есть message_code ошибки)
            if (result.data && result.data.code && result.data.code >= 400) {
                return rejectWithValue(result.data);
            }

            return result.data;
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateCandidate = createAsyncThunk(
    'candidates/update',
    async ({ id, organisationId, candidate }, { rejectWithValue }) => {
        try {
            return await candidatesApi.update(id, organisationId, candidate);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteCandidate = createAsyncThunk(
    'candidates/delete',
    async (id, { rejectWithValue }) => {
        try {
            return await candidatesApi.delete(id);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchCandidateHistory = createAsyncThunk(
    'candidates/fetchHistory',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await candidatesApi.getHistory(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const addCandidateFile = createAsyncThunk(
    'candidates/addFile',
    async ({ id, organisationId, file }, { rejectWithValue }) => {
        try {
            return await candidatesApi.addFile(id, organisationId, file);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchCandidateFiles = createAsyncThunk(
    'candidates/fetchFiles',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await candidatesApi.getFiles(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteCandidateFile = createAsyncThunk(
    'candidates/deleteFile',
    async (fileId, { rejectWithValue }) => {
        try {
            return await candidatesApi.delFiles(fileId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const initialState = {
    list: [],
    current: null,
    candidatesByVacancy: [],
    history: [],
    files: [],
    isLoading: false,
    historyLoading: false,
    filesLoading: false,
    code: null,
    message_code: null,
    error: null,
    duplicateCandidate: null,
    totalPages: null,
    totalItems: null
};

const candidatesSlice = createSlice({
    name: 'candidates',
    initialState,
    reducers: {
        clearCurrent: (state) => {
            state.current = null;
            state.error = null;
        },
        clearCandidateError: (state) => {
            state.code = null;
            state.message_code = null;
            state.error = null;
            state.duplicateCandidate = null;
        },
        updateCandidateStatusOptimistic: (state, action) => {
            const { vacancyId, candidateId, statusId } = action.payload;

            // Обновляем в списке кандидатов
            const listIndex = state.list.findIndex(item =>
                item.vacancy_id === vacancyId && item.candidate_id === candidateId
            );
            if (listIndex !== -1) state.list[listIndex].status_id = statusId;

            // Обновляем в текущем кандидате (если это тот же кандидат)
            if (state.current && state.current.id === candidateId) {
                const vacancyIndex = state.current.vacancies?.findIndex(v => v.id === vacancyId);
                if (vacancyIndex !== -1) {
                    state.current.vacancies[vacancyIndex].status_id = statusId;
                }
            }
        },
        revertCandidateStatus: (state, action) => {
            const { vacancyId, candidateId, originalStatusId } = action.payload;

            // Откатываем в списке кандидатов
            const listIndex = state.list.findIndex(item =>
                item.vacancy_id === vacancyId && item.candidate_id === candidateId
            );
            if (listIndex !== -1) state.list[listIndex].status_id = originalStatusId;

            // Откатываем в текущем кандидате (если это тот же кандидат)
            if (state.current && state.current.id === candidateId) {
                const vacancyIndex = state.current.vacancies?.findIndex(v => v.id === vacancyId);
                if (vacancyIndex !== -1) {
                    state.current.vacancies[vacancyIndex].status_id = originalStatusId;
                }
            }
        },
        // updateCandidateStatusOptimistic: (state, action) => {
        //     const { id, vacancy_id, status } = action.payload;

        //     // Упрощенная версия без лишних консолей
        //     const candidateIndex = state.list.findIndex(c => c.id === id);
        //     if (candidateIndex !== -1) {
        //         const vacancyIndex = state.list[candidateIndex].vacancies.findIndex(
        //             v => v.vacancy_id === vacancy_id
        //         );
        //         if (vacancyIndex !== -1) {
        //             state.list[candidateIndex].vacancies[vacancyIndex].status_id = status;
        //         }
        //     }
        // },
        // revertCandidateStatus: (state, action) => {
        //     const { id, originalStatus } = action.payload;
        //     const candidate = state.list.find(c => c.id === id);
        //     if (candidate) {
        //         candidate.status = originalStatus;
        //     }
        // }
    },
    extraReducers: (builder) => {
        builder

            // Get List
            .addCase(fetchCandidates.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCandidates.fulfilled, (state, action) => {
                state.isLoading = false;
                state.list = action.payload.items || action.payload;
                state.totalPages = action.payload.total_pages || null;
                state.totalItems = action.payload.total_items || null;
            })
            .addCase(fetchCandidates.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get by ID
            .addCase(fetchCandidateById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCandidateById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.current = action.payload;

                // Обновляем элемент в списке
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.list[index] = action.payload;
                }
            })
            .addCase(fetchCandidateById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Create
            .addCase(createCandidate.pending, (state) => {
                state.isLoading = true;
                // Очищаем предыдущие ошибки при новом запросе
                state.code = null;
                state.message_code = null;
                state.error = null;
                state.duplicateCandidate = null;
            })
            .addCase(createCandidate.fulfilled, (state, action) => {
                state.isLoading = false;
                // Очищаем ошибки при успешном создании
                state.code = null;
                state.message_code = null;
                state.error = null;
                state.duplicateCandidate = null;
            })
            .addCase(createCandidate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;

                // Если это ошибка дубликата кандидата
                if (action.payload && action.payload.message_code === 'CANDIDATE_IS_ALREADY_EXISTS') {
                    state.code = action.payload.code;
                    state.message_code = action.payload.message_code;
                    state.duplicateCandidate = action.payload.data;
                }
            })

            // Get by Vacancy
            .addCase(fetchCandidatesByVacancy.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCandidatesByVacancy.fulfilled, (state, action) => {
                state.isLoading = false;
                state.candidatesByVacancy = action.payload;
            })
            .addCase(fetchCandidatesByVacancy.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Update
            .addCase(updateCandidate.fulfilled, (state, action) => {
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) state.list[index] = action.payload;
            })

            // Delete
            .addCase(deleteCandidate.fulfilled, (state, action) => {
                state.list = state.list.filter(item => item.id !== action.meta.arg);
            })

            // Get History
            .addCase(fetchCandidateHistory.pending, (state) => {
                state.historyLoading = true;
            })
            .addCase(fetchCandidateHistory.fulfilled, (state, action) => {
                state.historyLoading = false;
                state.history = action.payload;
            })
            .addCase(fetchCandidateHistory.rejected, (state, action) => {
                state.historyLoading = false;
                state.error = action.payload;
            })

            // Get Files
            .addCase(fetchCandidateFiles.pending, (state) => {
                state.filesLoading = true;
            })
            .addCase(fetchCandidateFiles.fulfilled, (state, action) => {
                state.filesLoading = false;
                state.files = action.payload;
            })
            .addCase(fetchCandidateFiles.rejected, (state, action) => {
                state.filesLoading = false;
                state.error = action.payload;
            })

            // Add File
            .addCase(addCandidateFile.fulfilled, (state, action) => {
                state.files.push(action.payload);
            })

            // Delete File
            .addCase(deleteCandidateFile.fulfilled, (state, action) => {
                state.files = state.files.filter(file => file.id !== action.meta.arg);
            })
            // Обработка logout из authSlice
            .addCase(logout, (state) => {
                state.list = [];
                state.current = null;
                state.files = [];
                state.loading = false;
                state.isLoading = false;
                state.error = null;
            });
    }
});

export const { clearCurrent, clearCandidateError, updateCandidateStatusOptimistic, revertCandidateStatus } = candidatesSlice.actions;
export const candidatesReducer = candidatesSlice.reducer; 